// apps/client/src/hooks/useEdmDomain.ts
//
// Typed wrappers over the `edmDomainAction` callable (eDM Phase 1c S6, the
// tenant sending domain). Every request is `{ action, companyId, ...fields }`;
// every refusal is `HttpsError(code, message, { code: '<reason>' })`.
//
// Domain state is read ONLY through this callable: `edm_domains` and
// `companies/{c}/edm_settings` are client read/write false in firestore.rules.

import { useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export const EDM_DOMAIN_CALLABLE_OPTIONS = { timeout: 120_000 } as const;

export type EdmDomainStatus =
  | 'creating'
  | 'create_failed'
  | 'not_started'
  | 'pending'
  | 'verified'
  | 'failed'
  | 'temporary_failure';

export interface EdmDomainDnsRecord {
  record: string;
  name: string;
  type: string;
  value: string;
  ttl: string;
  status: string;
  priority?: number;
}

export interface EdmDomainRecord {
  name: string;
  companyId: string;
  resendDomainId: string | null;
  region: string;
  status: EdmDomainStatus;
  records: EdmDomainDnsRecord[];
  error?: string | null;
}

export interface EdmDomainState {
  domain: EdmDomainRecord | null;
  /** This environment's VALUTTEN eDM domain (stage pilot / tests), or null. */
  envDomain: string | null;
  isProd: boolean;
}

export interface EdmDomainApi {
  getDomain: () => Promise<EdmDomainState>;
  createDomain: (name: string) => Promise<{ domain: EdmDomainRecord }>;
  refreshDomain: () => Promise<{ domain: EdmDomainRecord }>;
  removeDomain: () => Promise<{ removed: true }>;
}

async function call<Res>(action: string, payload: Record<string, unknown>): Promise<Res> {
  const fn = httpsCallable<Record<string, unknown>, Res>(functions, 'edmDomainAction', EDM_DOMAIN_CALLABLE_OPTIONS);
  const res = await fn({ action, ...payload });
  return res.data;
}

/** The callable surface bound to one company. */
export function useEdmDomain(companyId: string | undefined): EdmDomainApi {
  const withCompany = useCallback(
    <Res,>(action: string, payload: Record<string, unknown> = {}) => {
      if (!companyId) return Promise.reject(new Error('companyId required'));
      return call<Res>(action, { companyId, ...payload });
    },
    [companyId],
  );

  return useMemo<EdmDomainApi>(
    () => ({
      getDomain: () => withCompany('getDomain'),
      createDomain: (name) => withCompany('createDomain', { name }),
      refreshDomain: () => withCompany('refreshDomain'),
      removeDomain: () => withCompany('removeDomain'),
    }),
    [withCompany],
  );
}
